// Block « À faire maintenant » — v2 cockpit (owner, 2026-10-22; v1 2026-10-21).
// ONE list instead of « Qui nous attend », « Albums à valider », « Prêts à
// expédier » and « Modifications demandées ». Never bound to the period picker.
// Longest wait first; a coloured left bar: red > 1 h, amber > 30 min, grey.
// Pills: « À moi + libres » (on by default) and one per verb with its count —
// a click filters. At most 6 rows, then one fold line « + N autres · M albums
// attendent depuis plus de 14 jours — Tout voir → ». Worker: GET /dashboard/todo.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi, type TodoItem } from '@/lib/dashboardApi';
import { formatWait, waitTone, viewTodo, verbCounts, formatDH, TODO_MAX_ROWS, type TodoMode } from '@/lib/homeDashboard';
import { useT } from '@/lib/i18n';
import { HomeCard, HomeCardTitle } from './homeParts';

type TFn = ReturnType<typeof useT>['t'];
type Kind = TodoItem['kind'];

export const HOME_TODO_KEY = ['home-todo'] as const;

const VERB: Record<Kind, string> = {
  validate: "Valider l'album", reply: 'Répondre', preview: "Envoyer l'aperçu", ship: 'Expédier',
};
const BUTTON: Record<Kind, string> = {
  validate: "Ouvrir l'album", reply: 'Ouvrir le chat', preview: "Voir l'aperçu", ship: 'Créer le colis',
};
/** Short pill names. */
const PILL: Record<Kind, string> = { validate: 'Valider', reply: 'Répondre', preview: 'Aperçu', ship: 'Expédier' };
const PILL_ORDER: Kind[] = ['validate', 'reply', 'preview', 'ship'];

const BAR = { red: 'bg-status-stuck-solid', orange: 'bg-[#F59E0B]', grey: 'bg-[#E5DFD2] dark:bg-adm-line-strong' } as const;
const WAIT = { red: 'text-status-stuck font-bold', orange: 'text-status-waiting font-bold', grey: 'text-adm-muted font-medium' } as const;

function detailText(item: TodoItem, t: TFn): string | null {
  const d = item.detail;
  if (!d) return null;
  if (d.kind === 'photos') return t('{n} photos', { n: d.n });
  if (d.kind === 'changes') return t('modification demandée');
  return `« ${d.text.length > 60 ? `${d.text.slice(0, 60)}…` : d.text} »`;
}

function TodoRow({ item, nowMs, t }: { item: TodoItem; nowMs: number; t: TFn }) {
  const tone = waitTone(item.since, nowMs);
  const detail = detailText(item, t);
  const isChat = item.kind === 'reply';
  return (
    <div data-todo-row
      className="grid grid-cols-[4px_minmax(0,1fr)_auto] md:grid-cols-[4px_minmax(0,1fr)_112px_54px_64px_112px] items-center gap-x-3 gap-y-[3px] py-[9px] md:py-[7px] border-t border-[#F3EEE3] dark:border-adm-line first:border-t-0">
      <i aria-hidden className={`w-1 rounded self-stretch md:self-auto md:h-[30px] row-span-3 md:row-span-1 ${BAR[tone]}`} />
      <div className="min-w-0 col-start-2 row-start-1 md:col-start-auto md:row-start-auto">
        <b className="block font-semibold text-[13px] text-adm-ink truncate">{item.customer_name || t('Cliente')}</b>
        <small className="block text-[11px] text-adm-faint truncate">{[item.order_ref, detail].filter(Boolean).join(' · ') || '—'}</small>
      </div>
      <span className="col-start-2 row-start-2 md:col-start-auto md:row-start-auto text-[12px] font-semibold text-adm-ink-2 truncate">{t(VERB[item.kind])}</span>
      <span className={`col-start-3 row-start-1 md:col-start-auto md:row-start-auto text-[12.5px] text-right tabular-nums whitespace-nowrap ${WAIT[tone]}`}
        title={new Date(item.since).toLocaleString('fr-FR')}>
        {formatWait(item.since, nowMs)}
      </span>
      <span className="col-start-3 row-start-2 md:col-start-auto md:row-start-auto text-[12px] text-adm-ink-2 text-right tabular-nums whitespace-nowrap">
        {item.amount != null ? formatDH(item.amount) : '—'}
      </span>
      <Link to={item.href}
        className={`col-start-2 col-span-2 row-start-3 md:col-start-auto md:col-span-1 md:row-start-auto whitespace-nowrap rounded-lg py-[9px] md:py-1.5 text-[11.5px] font-semibold text-center transition-opacity hover:opacity-90 ${
          isChat ? 'bg-[#128C7E] text-white' : 'bg-[#2B2116] text-[#FBF7EC] dark:bg-adm-gold dark:text-[#2B2116]'}`}>
        {t(BUTTON[item.kind])}
      </Link>
    </div>
  );
}

const pillCls = (on: boolean) => `text-[11.5px] px-[9px] py-[3px] rounded-full border whitespace-nowrap transition-colors ${on
  ? 'bg-adm-gold-wash border-[#EAD693] dark:border-adm-gold text-adm-gold-ink font-semibold'
  : 'border-[#ECE5D6] dark:border-adm-line text-adm-ink-2 font-medium hover:bg-adm-surface-2'}`;

/** The list for the data — split out so the DEV preview renders the very same thing. */
export function TodoBody({ items, nowMs }: { items: TodoItem[]; nowMs: number }) {
  const { t } = useT();
  const [mode, setMode] = useState<TodoMode>('mine');
  const [kind, setKind] = useState<Kind | null>(null);
  const [expanded, setExpanded] = useState(false);
  const { rows: modeRows, oldCount } = viewTodo(items, mode);
  const counts = verbCounts(modeRows);
  const rows = kind ? modeRows.filter((r) => r.kind === kind) : modeRows;
  const shown = expanded ? rows : rows.slice(0, TODO_MAX_ROWS);
  const hidden = rows.length - shown.length;

  const pills = (
    <div role="group" aria-label={t('Filtre')} className="hidden md:flex gap-1.5 min-w-0 overflow-hidden">
      <button type="button" aria-pressed={mode === 'mine'} className={pillCls(mode === 'mine')}
        onClick={() => { setMode(mode === 'mine' ? 'all' : 'mine'); setExpanded(false); }}>
        {t('À moi + libres')}
      </button>
      {PILL_ORDER.filter((k) => counts[k] > 0 || kind === k).map((k) => (
        <button key={k} type="button" aria-pressed={kind === k} className={pillCls(kind === k)}
          onClick={() => { setKind(kind === k ? null : k); setExpanded(false); }}>
          {t(PILL[k])} {counts[k]}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <HomeCardTitle label={t('À faire maintenant')} count={rows.length} right={pills} />
      {rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-adm-muted">✓ {t('Rien en attente')}</p>
      ) : (
        <div>{shown.map((it) => <TodoRow key={it.key} item={it} nowMs={nowMs} t={t} />)}</div>
      )}
      {(hidden > 0 || oldCount > 0) && (
        <div className="mt-1.5 flex items-center justify-between gap-2 rounded-lg bg-[#FBF7EC] dark:bg-adm-gold-wash px-2.5 py-[7px] text-[12px] text-adm-gold-ink">
          <span className="min-w-0">
            {[
              hidden > 0 ? t('+ {n} autres', { n: hidden }) : null,
              oldCount > 0 ? t('{n} albums attendent depuis plus de 14 jours', { n: oldCount }) : null,
            ].filter(Boolean).join(' · ')}
          </span>
          {hidden > 0 ? (
            <button type="button" onClick={() => setExpanded(true)} className="font-semibold whitespace-nowrap hover:underline">{t('Tout voir')} →</button>
          ) : (
            <Link to="/admin/rapports#dossiers-anciens" className="font-semibold whitespace-nowrap hover:underline">{t('Tout voir')} →</Link>
          )}
        </div>
      )}
      {expanded && oldCount > 0 && (
        <Link to="/admin/rapports#dossiers-anciens" className="mt-1 block text-right text-[12px] font-semibold text-adm-gold-ink hover:underline">
          {t('Voir les anciens')} →
        </Link>
      )}
    </>
  );
}

export function HomeTodoCard({ className = '' }: { className?: string }) {
  const { t } = useT();
  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: HOME_TODO_KEY,
    queryFn: () => dashboardApi.todo(),
    staleTime: 30_000,
    refetchInterval: 120_000,
  });
  // Waits are measured at the moment the list was read (refreshed every 2 min).
  const nowMs = dataUpdatedAt;
  return (
    <HomeCard id="home-todo" className={className}>
      {isLoading ? (
        <>
          <HomeCardTitle label={t('À faire maintenant')} />
          <div className="space-y-2 py-1" aria-busy>
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-9 rounded-lg bg-adm-surface-3 animate-pulse" />)}
          </div>
        </>
      ) : error || !data ? (
        <>
          <HomeCardTitle label={t('À faire maintenant')} />
          <p className="py-4 text-sm text-status-stuck">{t('Liste indisponible — réessayez dans un instant.')}</p>
        </>
      ) : (
        <TodoBody items={data.items} nowMs={nowMs} />
      )}
    </HomeCard>
  );
}
